import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Upload, Loader2 } from "lucide-react";
import { uploadEntryImage } from "@/lib/sacred-library/sacred-library-api";
import { uploadPageImage } from "@/lib/sacred-pages/sacred-pages-api";
import { SacredTopicImage } from "@/components/sacred-library/SacredTopicImage";

const MAX_DIM = 512;

function resizeImage(file, maxDim) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const r = Math.min(maxDim / width, maxDim / height);
        width = Math.round(width * r);
        height = Math.round(height * r);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      const mime = file.type === "image/png" ? "image/png" : "image/jpeg";
      resolve(canvas.toDataURL(mime, 0.88));
    };
    img.onerror = reject;
    img.src = url;
  });
}

export function ImageUploadPanel({ topicType, id, imageUrl, onUploaded, mode = "library" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(imageUrl);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      let dataUrl;
      if (file.type === "image/svg+xml") {
        const text = await file.text();
        dataUrl = `data:image/svg+xml;base64,${btoa(text)}`;
      } else {
        dataUrl = await resizeImage(file, MAX_DIM);
      }
      setPreview(dataUrl);
      const res =
        mode === "page"
          ? await uploadPageImage(id, dataUrl, file.type || "image/png")
          : await uploadEntryImage(topicType, id, dataUrl, file.type || "image/png");
      onUploaded?.(res.image_url || res.hero_image);
    } catch (err) {
      console.error(err);
      if (err?.response?.status === 401) {
        try {
          localStorage.removeItem("vedapath_sacred_editor_token");
          localStorage.removeItem("vedapath_sacred_editor_user");
        } catch {
          /* ignore */
        }
      }
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        err?.message ||
        "Sign in as admin/staff or complete editor setup at /admin/setup.";
      alert(`Upload failed — ${detail}`);
    }
    setUploading(false);
  };

  return (
    <div className="space-y-3 border rounded-lg p-4 bg-muted/20" data-testid="image-upload-panel">
      <SacredTopicImage type={topicType} src={preview} alt="Preview" size="detail" className="mx-auto max-w-[200px]" />
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/svg+xml"
        className="hidden"
        onChange={onFile}
      />
      <Button type="button" variant="secondary" className="w-full" onClick={() => inputRef.current?.click()} disabled={uploading}>
        {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
        Upload image
      </Button>
      <p className="text-[10px] text-muted-foreground text-center">PNG, JPG, or SVG — resized to max {MAX_DIM}px</p>
    </div>
  );
}
